//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

/**
 * Processes an array of items through an async callback in sequential chunks,
 * limiting the number of concurrent in-flight requests per chunk.
 *
 * Note: every chunk waits for its slowest member before the next chunk starts,
 * so the effective concurrency sags between chunks. Prefer
 * {@link mapWithConcurrency} for large fan-outs.
 *
 * @param items - The items to process
 * @param chunkSize - Maximum number of concurrent tasks per chunk (default 10)
 * @param callback - Async function to call for each item
 */
export async function chunkedMap<T>(
  items: T[],
  chunkSize: number,
  callback: (item: T) => Promise<void>,
): Promise<void> {
  for (let i = 0; i < items.length; i += chunkSize) {
    const chunk = items.slice(i, i + chunkSize);
    await Promise.all(chunk.map(callback));
  }
}

export interface MapWithConcurrencyOptions {
  /**
   * Polled before each item is started. Return true to stop scheduling new
   * work (already-started items still run to completion). Use this to abandon
   * a fan-out when the caller unmounts or the input becomes stale.
   */
  shouldStop?: () => boolean;
  /** Called after each item settles with the number completed so far. */
  onProgress?: (completed: number, total: number) => void;
}

/**
 * Processes items with a sliding window of at most `limit` tasks in flight.
 * As soon as one task settles the next item starts, so the pipeline stays full
 * instead of draining between batches like {@link chunkedMap}.
 *
 * Callback rejections are swallowed so one failure cannot abort the whole run;
 * callers that care about individual failures should catch inside `callback`.
 *
 * @param items - The items to process
 * @param limit - Maximum number of tasks in flight at once (min 1)
 * @param callback - Async function to call for each item
 * @param options - Optional stop predicate and progress hook
 */
export async function mapWithConcurrency<T>(
  items: T[],
  limit: number,
  callback: (item: T) => Promise<void>,
  options: MapWithConcurrencyOptions = {},
): Promise<void> {
  const total = items.length;
  if (total === 0) return;
  const workers = Math.max(1, Math.min(limit, total));
  let nextIndex = 0;
  let completed = 0;

  const worker = async (): Promise<void> => {
    while (nextIndex < total) {
      if (options.shouldStop?.()) return;
      const item = items[nextIndex++];
      try {
        await callback(item);
      } catch {
        // Per-item failures are the callback's responsibility; keep the pool running.
      }
      completed++;
      options.onProgress?.(completed, total);
    }
  };

  await Promise.all(Array.from({ length: workers }, worker));
}
